import { notification } from 'antd';
import type { FormInstance } from 'antd';

const errorMessageMap: Record<string, string> = {
  'Admin::Classrooms::ChangeEndsAtError':
    'Não é possível alterar a vigência de uma turma que já está fechada',
  'Admin::Classrooms::Evaluations::ChangeEndsAtError':
    'Não é possível alterar a vigência de uma avaliação em uma turma que já está fechada',
  'Admin::Classrooms::Evaluations::CreateToClosedClassroomError':
    'Não é possível criar avaliações ou atividades em uma turma que já está fechada',
  'Admin::Classrooms::Evaluations::InvalidPeriodError':
    'O período da avaliação deve estar dentro da vigência da turma.'
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleApiError = (error: any) => {
  try {
    const code = error?.message || error?.code;

    notification.error({
      message: 'Erro',
      // Never render an empty toast: most codes aren't in the map.
      description:
        errorMessageMap[code] ??
        'Ocorreu um erro ao processar sua solicitação. Tente novamente.'
    });
  } catch {
    notification.error({
      message: 'Erro inesperado',
      description:
        'Ocorreu um erro ao processar sua solicitação. Tente novamente.'
    });
  }
};

function isAntdFormValidationError(error: unknown): boolean {
  if (error === null || typeof error !== 'object') {
    return false;
  }
  return Array.isArray((error as { errorFields?: unknown }).errorFields);
}

function getFirstValidationErrorFieldName(
  error: unknown
): string | number | (string | number)[] | undefined {
  if (!isAntdFormValidationError(error)) {
    return undefined;
  }
  const first = (error as { errorFields?: { name?: unknown }[] })
    .errorFields?.[0];
  const name = first?.name;

  if (
    typeof name === 'string' ||
    typeof name === 'number' ||
    (Array.isArray(name) &&
      name.every(x => typeof x === 'string' || typeof x === 'number'))
  ) {
    return name as string | number | (string | number)[];
  }
  return undefined;
}

/**
 * Handles the *validation* phase of a multi-step form: scrolls to the first
 * invalid field, which may live on a step that isn't currently visible (the
 * user would otherwise see an inert submit button and no explanation). The
 * field itself already renders the message, so no toast is raised here.
 *
 * Deliberately NOT for mutation failures: those are reported by refine's
 * `errorNotification`, and routing them here too produced a duplicate toast.
 * Anything genuinely unexpected still falls through to `handleApiError`.
 */
export const handleWizardError = (form: FormInstance, error: unknown): void => {
  if (isAntdFormValidationError(error)) {
    const field = getFirstValidationErrorFieldName(error);

    if (field !== undefined) {
      form.scrollToField(field);
    }
    return;
  }
  handleApiError(error);
};
